/**
 * „Wie entstand das?" — die Herkunft eines KI-Vorschlags (KI-Spur, Etappe 3).
 *
 * An jeder Stelle, an der Clara oder der Belegleser etwas vorschlägt, lässt sich
 * nachsehen: welches Modell, unter welcher Fassung der Anweisung, wie lange,
 * welche Quellen es gelesen hat (Mails springen in den Verlauf) und was ein
 * Mensch daraus gemacht hat. Das Denken des Modells und die vollständige
 * Eingabe stehen hinter einem zweiten Aufklappen — sie sind lang und enthalten
 * Kundentexte. Das Denken wird nach 90 Tagen geleert (Benjamins Frist).
 *
 * Nur lesend. Paperclip ist Benjamins Leitstand; hier sieht auch Volker nach.
 */
import { fehlerText } from "@vimaana/ui";
import { useState } from "react";

import {
  KI_ART, KI_EREIGNIS, kiDauer, kiModell, kiTeil, useKiAuftrag, useKiZuBezug,
  type KiAuftrag, type KiQuelle,
} from "../../hooks/useKiSpur";
import { zuMail } from "../../lib/sprung";
import "../../styles/ki-spur.css";

const QUELLE: Record<string, string> = {
  mail: "Mail", termin: "Termin", dokument: "Beleg", akte: "Notiz", kosten: "Ausgabe", wissen: "Wissen",
  beleg: "Beleg", seite: "Seite", status: "Status",
};

function zeit(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" });
}

function tag(iso?: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso.slice(0, 10) : d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" });
}

function QuellenListe({ quellen }: { quellen: KiQuelle[] }) {
  const [alle, setAlle] = useState(false);
  if (!quellen.length) return <p className="ki-leer">Keine Quellen festgehalten (vor dem 28.09.2026 entstanden).</p>;
  const zeigen = alle ? quellen : quellen.slice(0, 8);
  return (
    <>
      <ul className="ki-quellen">
        {zeigen.map((q, i) => (
          <li key={`${q.typ}:${q.id}:${i}`}>
            <span className="ki-quelle-art">{QUELLE[q.typ] || q.typ}</span>
            <span className="ki-quelle-titel">{q.titel || q.id}</span>
            {q.zeitpunkt && <span className="ki-quelle-tag">{tag(q.zeitpunkt)}</span>}
            {q.abgeschnitten && <span className="ki-marke is-warn" title="Vom Zeichendeckel gekürzt — nicht ganz gelesen">gekürzt</span>}
            {q.nachgelesen && <span className="ki-marke">nachgelesen</span>}
            {q.typ === "mail" && q.id && (
              <button type="button" className="vg-post-link ki-quelle-sprung" onClick={() => zuMail(q.id)}>zeigen</button>
            )}
          </li>
        ))}
      </ul>
      {quellen.length > 8 && (
        <button type="button" className="vg-post-link" onClick={() => setAlle((v) => !v)}>
          {alle ? "weniger" : `alle ${quellen.length} Quellen`}
        </button>
      )}
    </>
  );
}

function Ereignisse({ a }: { a: KiAuftrag }) {
  if (!a.ereignisse.length) {
    return <p className="ki-leer">{a.status === "fertig" || a.status === "angewandt" ? "Noch keine Entscheidung festgehalten." : "Noch nicht fertig gerechnet."}</p>;
  }
  return (
    <ul className="ki-ereignisse">
      {a.ereignisse.map((e, i) => {
        const vergleich = (e.art === "geaendert" || e.art === "gesendet") && e.text_vorher && e.text_nachher && e.text_vorher !== e.text_nachher;
        return (
          <li key={i}>
            <span className="ki-ereignis-zeit">{zeit(e.am)}</span>
            <span>
              {e.wer ? `${e.wer.split(" ")[0]} · ` : ""}{kiTeil(e.teil) ? `${kiTeil(e.teil)} ` : ""}{KI_EREIGNIS[e.art] || e.art}
              {e.art === "gesendet" && e.text_vorher === e.text_nachher && " — unverändert"}
            </span>
            {vergleich && (
              <details className="ki-vergleich">
                <summary>Vorschlag und Fassung nebeneinander</summary>
                <div className="ki-vergleich-spalten">
                  <div><small>Vorschlag</small><pre>{e.text_vorher}</pre></div>
                  <div><small>{e.art === "gesendet" ? "gesendet" : "übernommen"}</small><pre>{e.text_nachher}</pre></div>
                </div>
              </details>
            )}
          </li>
        );
      })}
    </ul>
  );
}

/** Der Inhalt der Klappe — auch auf der Seite „Claras Protokoll" benutzt. */
export function KiHerkunftInhalt({ auftragId }: { auftragId: string }) {
  const q = useKiAuftrag(auftragId);
  const [denken, setDenken] = useState(false);
  const [eingabe, setEingabe] = useState(false);
  if (q.isLoading) return <p className="ki-leer">Lädt …</p>;
  if (q.error) return <p className="vg-post-fehler">{fehlerText(q.error)}</p>;
  const a = q.data;
  if (!a) return null;
  const fakten: [string, string][] = [
    ["Art", KI_ART[a.art] || a.art],
    ["Gerechnet mit", kiModell(a.modell) || (a.status === "fehler" ? "—" : "noch offen")],
    ["Anweisung", a.vorlage || "vor dem 28.09.2026"],
    ["Rechenzeit", kiDauer(a.rechenzeit_ms)],
    ["Tokens", a.tokens_ein != null ? `${a.tokens_ein.toLocaleString("de-DE")} gelesen · ${(a.tokens_aus ?? 0).toLocaleString("de-DE")} geschrieben` : ""],
    ["Entstanden", zeit(a.fertig_am || a.created_at)],
    ["Paperclip", a.paperclip_ticket || ""],
  ];
  return (
    <div className="ki-inhalt">
      <dl className="ki-fakten">
        {fakten.filter(([, w]) => w).map(([k, w]) => (
          <div key={k}><dt>{k}</dt><dd>{w}</dd></div>
        ))}
      </dl>
      {a.fehler && <p className="ki-leer">Letzter Fehler: {a.fehler}</p>}

      <h5 className="ki-titel">Gelesen · {a.quellen.length} {a.quellen.length === 1 ? "Quelle" : "Quellen"}</h5>
      <QuellenListe quellen={a.quellen} />

      <h5 className="ki-titel">Was daraus wurde</h5>
      <Ereignisse a={a} />

      <div className="ki-weiter">
        {a.denken ? (
          <button type="button" className="vg-post-link" aria-expanded={denken} onClick={() => setDenken((v) => !v)}>
            {denken ? "Gedankengang verbergen" : "Claras Gedankengang"}
          </button>
        ) : (
          <span className="ki-leer">Kein Gedankengang festgehalten{a.fertig_am ? " (vor Etappe 2 oder nach 90 Tagen geleert)" : ""}.</span>
        )}
        {a.prompt && (
          <button type="button" className="vg-post-link" aria-expanded={eingabe} onClick={() => setEingabe((v) => !v)}>
            {eingabe ? "Eingabe verbergen" : "Vollständige Eingabe"}
          </button>
        )}
      </div>
      {denken && a.denken && <pre className="ki-lang">{a.denken}</pre>}
      {eingabe && a.prompt && <pre className="ki-lang">{a.prompt}</pre>}
    </div>
  );
}

/**
 * Die Klappe. `auftragId`: ein bestimmter Auftrag (etwa ein Mailentwurf).
 * `bezugId`: der jüngste Auftrag zu diesem Bezug (etwa ein Beleg) — erst beim Aufklappen gesucht.
 */
export default function KiHerkunft({ auftragId, bezugId, knopf = "Wie entstand das?" }: {
  auftragId?: string; bezugId?: string; knopf?: string;
}) {
  const [auf, setAuf] = useState(false);
  const zuBezug = useKiZuBezug(bezugId, auf && !auftragId);
  if (!auftragId && !bezugId) return null;
  const id = auftragId || zuBezug.data?.auftraege?.[0]?.id || "";
  return (
    <div className="ki-herkunft">
      <button type="button" className="vg-post-link ki-herkunft-knopf" aria-expanded={auf} onClick={() => setAuf((v) => !v)}>
        {auf ? "Herkunft schließen" : knopf}
      </button>
      {auf && (id
        ? <KiHerkunftInhalt auftragId={id} />
        : <p className="ki-leer">{zuBezug.isLoading ? "Sucht …" : "Hierzu hat Clara nichts festgehalten."}</p>)}
    </div>
  );
}
